import { useI18n } from '../../i18n';
// ============================================
// ReportsPage - Main Reports Dashboard Page
// Layout matches design: header, filters, KPIs, charts, tables
// ============================================

import { useMemo, useState, useRef, useEffect } from 'react';
import { PageContainer, PageHeader } from '../../components/shared';
import { useStoreSelectionStore } from '../../stores';
import {
  useExport,
  useRevenueByStores,
  useRevenueTrend,
  useTopProducts,
  useTopLostItems,
  useIsMixedCurrencyScope,
  useActiveCurrency,
} from '../../hooks';
import { useCurrencyScope } from '../../providers/CurrencyProvider';
import type { LostItemReportRow, TopProductItem } from '../../hooks/useReports';
import { extractList } from '../../utils/apiList';
import { formatMoney } from '../../utils/currency';
import {
  ReportKPICards,
  RevenueOverTimeChart,
  RevenueByRegionPanel,
  DailyTransactionsChart,
  ProfitByLocationTable,
  TopProfitableProductsTable,
  TopLostItemsTable,
  type ReportKPI,
  type RevenueOverTimeData,
  type RevenueByRegionData,
  type DailyTransactionData,
  type ProfitByLocationRow,
  type TopProfitableProduct,
  type TopLostItem,
} from './components';


/**
 * Số dòng lấy về cho các bảng của trang Reports.
 *
 * Các endpoint này trả trọn gói (không phân trang server), FE phân trang phía
 * client 10 dòng/trang. Lấy 50 để có tối đa 5 trang — đủ sâu cho báo cáo "top N"
 * mà vẫn là một request nhẹ.
 */
const REPORT_TABLE_FETCH_LIMIT = 50;

// ============================================
// Component
// ============================================

export function ReportsPage() {
  const { t } = useI18n();
  const [dateRange, setDateRange] = useState('14');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setDatePickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { selectedStoreId, availableStores, setSelectedStoreById } = useStoreSelectionStore();

  const activeCurrency = useActiveCurrency();

  // Phạm vi đang xem có trộn nhiều loại tiền không (mục #75).
  //
  // Nguồn sự thật là CỜ CỦA BE (`mixed_currencies` trên response báo cáo) —
  // BE biết chính xác dữ liệu trong phạm vi gồm những tiền tệ nào, còn FE suy
  // từ danh sách cửa hàng chỉ là xấp xỉ (cửa hàng khai PLN nhưng kỳ này chưa
  // phát sinh đơn nào thì không nên tính là trộn).
  // `useIsMixedCurrencyScope()` giữ làm dự phòng cho bản BE cũ chưa trả cờ.
  const fallbackMixed = useIsMixedCurrencyScope();

  // Compute YYYY-MM-DD for API export params
  const { startDateStr, endDateStr } = useMemo(() => {
    const today = new Date();
    const fmtIso = (d: Date) => d.toISOString().slice(0, 10);
    
    if (dateRange === 'custom') {
      return {
        startDateStr: customStart || fmtIso(today),
        endDateStr: customEnd || fmtIso(today),
      };
    }
    
    const days = parseInt(dateRange, 10) || 14;
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    return {
      startDateStr: fmtIso(from),
      endDateStr: fmtIso(today),
    };
  }, [dateRange, customStart, customEnd]);

  const { triggerExport, isExporting, progress: exportProgress } = useExport({
    endpoint: '/reports/daily-sales/',
    params: { 
      store: selectedStoreId ?? undefined,
      start_date: startDateStr,
      end_date: endDateStr
    },
    filePrefix: 'sales_report',
  });

  // Compute preset options relative to today
  const today = new Date();
  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const presets = [7, 14, 28, 60].map((days) => {
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    return { value: String(days), label: `${t('daysAgo', { count: days })}: ${fmtDate(from)} - ${fmtDate(today)}` };
  });

  const selectedLabel = dateRange === 'custom'
    ? (customStart && customEnd ? `${customStart} - ${customEnd}` : 'Select Date')
    : (presets.find((p) => p.value === dateRange)?.label ?? 'Select Date');

  // ===== Data thật từ API — không còn mock/fallback =====
  // Doanh thu ở đây dùng mặc định BE (COMPLETED + PAID) = tiền đã thu thật,
  // đồng nhất với Revenue chart và Total Transactions trên Dashboard.

  const { data: currentTrend } = useRevenueTrend({
    start_date: startDateStr,
    end_date: endDateStr,
    store_id: selectedStoreId ?? undefined,
  });

  // Tiền tệ hiển thị của trang: ưu tiên `currency` BE trả cho phạm vi (chỉ có
  // nghĩa khi KHÔNG trộn), thiếu thì theo cửa hàng đang chọn (sheet 2 #75).
  useCurrencyScope(currentTrend?.currency ?? activeCurrency);

  // Trộn tiền tệ: ưu tiên cờ BE, thiếu thì mới suy từ danh sách cửa hàng.
  const isMixedCurrency = currentTrend?.mixed_currencies ?? fallbackMixed.isMixed;
  /** Tổng tách theo từng loại tiền — con số DÙNG ĐƯỢC khi phạm vi trộn tiền tệ. */
  const totalsByCurrency = currentTrend?.totals_by_currency;
  const mixedCurrencies =
    currentTrend?.currencies ??
    (totalsByCurrency ? Object.keys(totalsByCurrency) : fallbackMixed.currencies);

  // Kỳ liền trước cùng độ dài — baseline cho % thay đổi trên KPI.
  const previousRange = useMemo(() => {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return { start_date: startDateStr, end_date: endDateStr };
    }
    const lengthMs = end.getTime() - start.getTime() + 24 * 60 * 60 * 1000;
    const fmtIso = (d: Date) => d.toISOString().slice(0, 10);
    return {
      start_date: fmtIso(new Date(start.getTime() - lengthMs)),
      end_date: fmtIso(new Date(end.getTime() - lengthMs)),
    };
  }, [startDateStr, endDateStr]);

  const { data: previousTrend } = useRevenueTrend({
    start_date: previousRange.start_date,
    end_date: previousRange.end_date,
    store_id: selectedStoreId ?? undefined,
  });

  // Năm nay theo tháng + trọn năm ngoái, cho biểu đồ Revenue Over Time.
  const currentYear = new Date().getFullYear();
  const todayIsoStr = new Date().toISOString().slice(0, 10);

  const { data: thisYearTrend } = useRevenueTrend({
    start_date: `${currentYear}-01-01`,
    end_date: todayIsoStr,
    period: 'monthly',
    store_id: selectedStoreId ?? undefined,
  });

  const { data: lastYearTrend } = useRevenueTrend({
    start_date: `${currentYear - 1}-01-01`,
    end_date: `${currentYear - 1}-12-31`,
    period: 'monthly',
    store_id: selectedStoreId ?? undefined,
  });

  // limit 50 chứ không phải 10: bảng đã có phân trang 10 dòng/trang, giữ limit
  // bằng đúng một trang thì nút chuyển trang không bao giờ xuất hiện.
  const { data: topProductsData } = useTopProducts({
    start_date: startDateStr,
    end_date: endDateStr,
    store_id: selectedStoreId ?? undefined,
    limit: REPORT_TABLE_FETCH_LIMIT,
  });

  // Doanh thu theo từng cửa hàng (thay cho "Revenue by Region" mock cũ).
  const { data: storeRevenues } = useRevenueByStores(availableStores, {
    start_date: startDateStr,
    end_date: endDateStr,
  });

  // Top sản phẩm thất thoát (v1: session ABANDONED — khách rời không thanh toán).
  // Env chưa deploy /reports/lost-items/ thì query lỗi -> bảng trống.
  const { data: topLostItemsData } = useTopLostItems({
    start_date: startDateStr,
    end_date: endDateStr,
    store_id: selectedStoreId ?? undefined,
    limit: REPORT_TABLE_FETCH_LIMIT,
  });

  const revenue = parseFloat(currentTrend?.totals?.revenue ?? '0') || 0;
  const transactions = currentTrend?.totals?.order_count ?? 0;
  const previousRevenue = parseFloat(previousTrend?.totals?.revenue ?? '0') || 0;
  const previousTransactions = previousTrend?.totals?.order_count ?? 0;

  // Cost/profit do BE tính (profit = revenue - cost). Field optional để tương
  // thích BE cũ: thiếu field -> null -> UI hiển thị "—" như trước.
  const toMoney = (v?: string): number | null => {
    if (v == null) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const cost = toMoney(currentTrend?.totals?.cost);
  const profit = toMoney(currentTrend?.totals?.profit);
  const previousCost = toMoney(previousTrend?.totals?.cost);
  const previousProfit = toMoney(previousTrend?.totals?.profit);

  const fmtMoney = (v: number) => formatMoney(v, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  // Delta thật so với kỳ liền trước; không có baseline thì để trống (ẩn).
  const deltaOf = (current: number, baseline: number): { delta: string; deltaType: ReportKPI['deltaType'] } => {
    if (baseline <= 0) return { delta: '', deltaType: 'neutral' };
    const pct = ((current - baseline) / baseline) * 100;
    return {
      delta: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% ${t('vsPreviousPeriod')}`,
      deltaType: pct >= 0 ? 'positive' : 'negative',
    };
  };

  const kpis: ReportKPI[] = useMemo(() => {
    const avgTransaction = transactions > 0 ? revenue / transactions : 0;
    return [
      { label: t('revenue'), value: fmtMoney(revenue), ...deltaOf(revenue, previousRevenue), icon: 'revenue' },
      // cost/profit từ BE (/reports/revenue/); BE cũ chưa có field -> "—".
      {
        label: t('costPrice'),
        value: cost == null ? '—' : fmtMoney(cost),
        ...(cost != null && previousCost != null ? deltaOf(cost, previousCost) : { delta: '', deltaType: 'neutral' as const }),
        icon: 'cost',
      },
      {
        label: t('profit'),
        value: profit == null ? '—' : fmtMoney(profit),
        ...(profit != null && previousProfit != null ? deltaOf(profit, previousProfit) : { delta: '', deltaType: 'neutral' as const }),
        icon: 'profit',
      },
      { label: t('totalOrders'), value: transactions.toLocaleString(), ...deltaOf(transactions, previousTransactions), icon: 'transactions' },
      { label: t('avgTransaction').replace('. ', ' '), value: fmtMoney(avgTransaction), delta: '', deltaType: 'neutral', icon: 'margin' },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenue, transactions, previousRevenue, previousTransactions, cost, profit, previousCost, previousProfit, t]);

  const MONTH_LABELS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  const revenueOverTimeData: RevenueOverTimeData[] = useMemo(() => {
    // ⚠️ Đa tiền tệ (mục #75): vòng lặp này CỘNG `item.revenue` của mọi dòng
    // trong tháng. Khi phạm vi trộn tiền tệ, đường biểu đồ là tổng cộng chéo —
    // sai, nhưng trông bình thường.
    //
    // Hiện chỉ chặn được bằng banner cảnh báo ở đầu trang (nói rõ các con số
    // bên dưới cộng gộp nhiều loại tiền). Sửa triệt để cần `/reports/revenue/`
    // trả `currency` trên TỪNG DÒNG của `data[]` — lúc đó tách thành nhiều
    // đường, mỗi tiền tệ một đường. Đã hỏi BE.
    //
    // KHÔNG tự quy đổi tỷ giá ở FE: app không có nguồn tỷ giá, và quy đổi
    // theo tỷ giá hôm nay cho doanh thu năm ngoái là bịa số.
    const byMonth = (trend: typeof thisYearTrend): number[] => {
      const values = Array(12).fill(0);
      for (const item of trend?.data ?? []) {
        const date = new Date(item.date);
        if (!Number.isNaN(date.getTime())) {
          values[date.getMonth()] += parseFloat(item.revenue) || 0;
        }
      }
      return values;
    };
    const thisYearValues = byMonth(thisYearTrend);
    const lastYearValues = byMonth(lastYearTrend);
    return MONTH_LABELS.map((month, i) => ({
      month,
      thisYear: thisYearValues[i],
      lastYear: lastYearValues[i],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisYearTrend, lastYearTrend]);

  const revenueByRegion: RevenueByRegionData[] = useMemo(
    () =>
      [...(storeRevenues ?? [])]
        .sort((a, b) => b.revenue - a.revenue)
        .map((store) => ({ name: store.storeName, value: store.revenue, color: '#0288D1' })),
    [storeRevenues]
  );

  const dailyTransactions: DailyTransactionData[] = useMemo(
    () =>
      (currentTrend?.data ?? []).map((item) => {
        const date = new Date(item.date);
        return {
          date: Number.isNaN(date.getTime())
            ? item.date
            : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
          count: item.order_count ?? 0,
        };
      }),
    [currentTrend]
  );

  // Ưu tiên `by_store` từ /reports/revenue/ (BE tính sẵn cost/profit theo cửa
  // hàng trong MỘT request). Không có thì rơi về useRevenueByStores — hỏi từng
  // cửa hàng một.
  //
  // Bug #80: nhánh fallback trước đây gán CỨNG `profit: null, costPrice: null`,
  // nên bất cứ khi nào BE không trả `by_store` là hai cột đó luôn "—" — dù mỗi
  // response per-store vẫn có `totals.cost` và `totals.profit`. Nay nhánh này
  // đọc đúng hai số đó (xem `StoreRevenueSummary`), và chỉ còn "—" khi BE thật
  // sự không gửi gì.
  const profitByLocation: ProfitByLocationRow[] = useMemo(() => {
    const byStore = currentTrend?.by_store;
    if (byStore && byStore.length > 0) {
      return byStore.map((s) => ({
        location: s.store_name,
        transactions: s.order_count,
        revenue: parseFloat(s.revenue) || 0,
        profit: toMoney(s.profit),
        costPrice: toMoney(s.cost),
      }));
    }
    return (storeRevenues ?? []).map((store) => ({
      location: store.storeName,
      transactions: store.orderCount,
      revenue: store.revenue,
      profit: store.profit,
      costPrice: store.cost,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrend, storeRevenues]);

  // Bug #75: bảng luôn hiện "No sales in selected period" vì chỉ đọc `.data`/
  // `.results`. Interceptor của api-client đã bóc envelope, nên khi BE trả
  // { code, message, data: [...] } KHÔNG kèm `meta` thì giá trị nhận được là một
  // MẢNG TRẦN — cả hai key đều undefined. Dashboard chạy đúng vì dùng extractList;
  // trang này giờ dùng chung helper đó.
  const topProducts: TopProfitableProduct[] = useMemo(
    () =>
      extractList<TopProductItem>(topProductsData).map((item) => ({
        code: item.product_sku || item.product_id,
        name: item.product_name,
        quantity: item.quantity_sold,
        revenue: parseFloat(item.revenue) || 0,
        profit: item.profit != null ? parseFloat(item.profit) || 0 : null,
        costPrice: item.cost != null ? parseFloat(item.cost) || 0 : null,
      })),
    [topProductsData]
  );

  // Cùng lỗi payload với topProducts ở trên.
  const topLostItems: TopLostItem[] = useMemo(
    () =>
      extractList<LostItemReportRow>(topLostItemsData).map((item) => ({
        code: item.product_sku || item.product_id,
        name: item.product_name,
        quantityLost: item.quantity_lost,
        incidents: item.incident_count,
        lostValue: parseFloat(item.lost_value) || 0,
        currency: item.currency,
        lastIncidentAt: item.last_incident_at,
      })),
    [topLostItemsData]
  );

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        title={t('report')}
        sectionLabel="ANALYZE REVENUE AND PROFIT"
        actions={
          <div className="flex items-center gap-3">
            <button 
              onClick={() => triggerExport('xlsx')}
              disabled={isExporting}
              className="flex items-center gap-2 rounded-lg border border-[#0288D1] bg-white px-4 py-2 text-sm font-semibold text-[#0288D1] hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15.534 1.36L14.309 0H4.662C3.966 0 3.697 0.516 3.697 0.919V4.549H5.05V1.653C5.05 1.499 5.18 1.369 5.33 1.369H12.233C12.385 1.369 12.461 1.396 12.461 1.521V6.341H17.374C17.567 6.341 17.642 6.441 17.642 6.587V18.357C17.642 18.603 17.542 18.64 17.392 18.64H5.33C5.25562 18.6382 5.18485 18.6076 5.13261 18.5546C5.08037 18.5016 5.05076 18.4304 5.05 18.356V17.28H3.706V18.975C3.688 19.575 4.008 20 4.662 20H18.06C18.76 20 18.999 19.493 18.999 19.031V5.187L18.649 4.807L15.534 1.36ZM13.836 1.52L14.223 1.954L16.819 4.807L16.962 4.98H14.309C14.109 4.98 13.9823 4.94667 13.929 4.88C13.8757 4.81467 13.8447 4.71033 13.836 4.567V1.52ZM12.746 10.667H17.323V12.001H12.745L12.746 10.667ZM12.746 8.001H17.323V9.334H12.745L12.746 8.001ZM12.746 13.334H17.323V14.668H12.745L12.746 13.334ZM1 5.626V16.293H11.465V5.626H1ZM6.233 11.83L5.593 12.808H6.233V14H3.016L5.35 10.49L3.282 7.334H5.01L6.234 9.17L7.457 7.334H9.184L7.112 10.49L9.449 14H7.656L6.233 11.83Z" fill="#0288D1" />
              </svg>
              {isExporting ? `${t('exporting') || 'Exporting...'} ${exportProgress}%` : t('exportExcel')}
            </button>
            <button 
              onClick={() => triggerExport('pdf')}
              disabled={isExporting}
              className="flex items-center gap-2 rounded-lg bg-[#008CD2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#003a9e] transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17.924 7.154H17.41L17.437 5.264C17.4341 5.15347 17.3915 5.04768 17.317 4.966L12.901 0.134C12.8653 0.0937799 12.8218 0.0611823 12.7732 0.0381578C12.7245 0.0151333 12.6718 0.00215516 12.618 0L3.37801 0C3.17084 0.00332606 2.97304 0.0869012 2.82625 0.233132C2.67946 0.379363 2.59513 0.576843 2.59101 0.784V7.154H2.07601C1.79101 7.154 1.51601 7.272 1.31601 7.482C1.11195 7.69543 0.998667 7.97972 1.00001 8.275V14.105C1.00001 14.723 1.48201 15.225 2.07601 15.225H2.59101V19.215C2.59489 19.4227 2.67938 19.6207 2.82663 19.7672C2.97387 19.9137 3.17232 19.9972 3.38001 20H16.658C17.073 20 17.438 19.648 17.438 19.216V15.226H17.925C18.519 15.226 19.001 14.723 19.001 14.104V8.274C19.001 7.978 18.888 7.692 18.686 7.482C18.5882 7.37868 18.4703 7.29633 18.3397 7.23995C18.209 7.18356 18.0683 7.15432 17.926 7.154M3.95001 1.378H10.906V5.955C10.9056 6.05806 10.945 6.15728 11.016 6.232C11.0504 6.2682 11.0919 6.29706 11.1377 6.31682C11.1836 6.33659 11.233 6.34685 11.283 6.347H16.042V7.154H3.95001V1.378ZM3.95001 18.622V15.225H16.042V18.622H3.95001ZM12.291 1.52L12.676 1.954L15.256 4.807L15.399 4.98H12.762C12.562 4.98 12.436 4.94667 12.384 4.88C12.3307 4.81467 12.2997 4.71033 12.291 4.567V1.52ZM3.00001 14.232V8.232H4.91801C5.64468 8.232 6.11801 8.262 6.33801 8.322C6.67801 8.412 6.96235 8.608 7.19101 8.91C7.41968 9.21067 7.53401 9.6 7.53401 10.078C7.53401 10.446 7.46801 10.756 7.33601 11.008C7.20401 11.258 7.03635 11.4547 6.83301 11.598C6.64773 11.7339 6.43682 11.8309 6.21301 11.883C5.92768 11.9403 5.51468 11.969 4.97401 11.969H4.19501V14.232H3.00001ZM4.19501 9.247V10.95H4.84901C5.32035 10.95 5.63535 10.9187 5.79401 10.856C5.94735 10.7975 6.07866 10.6927 6.16969 10.5561C6.26073 10.4196 6.30698 10.258 6.30201 10.094C6.30941 9.89643 6.2415 9.70341 6.11201 9.554C5.98731 9.41284 5.81781 9.31891 5.63201 9.288C5.49001 9.26133 5.20335 9.248 4.77201 9.248L4.19501 9.247ZM8.23501 8.232H10.419C10.9117 8.232 11.2873 8.27033 11.546 8.347C11.8927 8.45033 12.19 8.63433 12.438 8.899C12.6853 9.16433 12.8737 9.48833 13.003 9.871C13.133 10.255 13.1977 10.7277 13.197 11.289C13.197 11.783 13.1363 12.2087 13.015 12.566C12.8663 13.0027 12.655 13.356 12.381 13.626C12.1743 13.8313 11.8943 13.9913 11.541 14.106C11.2777 14.19 10.9253 14.232H10.484 14.232H8.23501V8.232ZM9.43001 9.247V13.221H10.322C10.656 13.221 10.897 13.202 11.045 13.164C11.239 13.1147 11.3997 13.0313 11.527 12.914C11.6557 12.7967 11.76 12.6037 11.84 12.335C11.9207 12.0663 11.961 11.236C11.961 10.772 11.921 10.416 11.841 10.168C11.7766 9.94956 11.6599 9.75013 11.501 9.587C11.3467 9.44286 11.1552 9.34482 10.948 9.304C10.7813 9.266 10.4547 9.247 9.96801 9.247H9.43001ZM13.943 14.232V8.232H18V9.247H15.138V10.667H17.608V11.682H15.138V14.232H13.943Z" fill="white" />
              </svg>
              {isExporting ? `${t('exporting') || 'Exporting...'} ${exportProgress}%` : t('reportPdf')}
            </button>
          </div>
        }
      />

      {/* Cảnh báo trộn tiền tệ (sheet 2 mục #75).
          Báo cáo này CỘNG GỘP doanh thu/lợi nhuận của mọi cửa hàng trong phạm
          vi đang xem. Chuỗi nhiều thị trường (PL dùng PLN, UA dùng UAH) mà
          cộng thẳng thì con số ra vô nghĩa — mà lại trông hoàn toàn bình
          thường, nên không ai phát hiện. Chỉ hiện khi đang xem TOÀN CHUỖI và
          các cửa hàng thật sự khai từ hai loại tiền trở lên; chuỗi một thị
          trường không thấy cảnh báo thừa.
          Chưa tách được tổng theo từng loại tiền vì endpoint báo cáo trả một
          con số gộp — đã hỏi BE (xem tin nhắn 03/09). */}
      {isMixedCurrency && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#b45309" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M12 9v4M12 17h.01" stroke="#b45309" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-amber-900">{t('mixedCurrencyTitle')}</p>
            <p className="mt-0.5 text-xs text-amber-800">
              {t('mixedCurrencyBody', { currencies: mixedCurrencies.join(', ') })}
            </p>

            {/* BE trả `totals_by_currency` (03/09) thì hiện luôn con số DÙNG
                ĐƯỢC ngay tại đây, thay vì chỉ bảo người ta "hãy chọn một cửa
                hàng". Bản BE cũ chưa có field thì phần này tự ẩn. */}
            {totalsByCurrency && Object.keys(totalsByCurrency).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                {Object.entries(totalsByCurrency).map(([code, totals]) => (
                  <span key={code} className="text-xs text-amber-900">
                    <span className="font-bold">
                      {formatMoney(Number(totals.revenue), { currency: code })}
                    </span>
                    <span className="ml-1 opacity-75">
                      ({totals.order_count} {t('ordersLower') || 'orders'})
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="text-xs font-medium text-[#434655] sm:text-sm">{t('selectDateRange').replace('Select', 'Date')}:</span>
          <div className="relative flex-1 sm:flex-none" ref={datePickerRef}>
            <button
              onClick={() => setDatePickerOpen((v) => !v)}
              className="flex h-9 w-full items-center justify-between gap-1 rounded-lg border border-[#E2E8F0] bg-white pl-3 pr-3 text-xs text-[#191B23] outline-none hover:border-slate-300 sm:w-64 sm:text-sm"
            >
              <span className={dateRange ? 'text-[#191B23]' : 'text-[#94A3B8]'}>{selectedLabel}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="3.333" width="12" height="11.333" rx="1.333" stroke="#94A3B8" strokeWidth="1.2" /><path d="M5.333 2v2.667M10.667 2v2.667M2 6.667h12" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round" /></svg>
            </button>

            {datePickerOpen && (
              <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-xl border border-slate-100 bg-white py-2 shadow-xl sm:w-72">
                {presets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => {
                      setDateRange(dateRange === preset.value ? '' : preset.value);
                      setShowCustom(false);
                      setDatePickerOpen(false);
                    }}
                    className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <span>{preset.label}</span>
                    <span className={`h-4 w-4 rounded-full border-2 ${dateRange === preset.value ? 'border-slate-500 bg-slate-500' : 'border-slate-300'}`} />
                  </button>
                ))}
                <div className="mx-4 my-1 border-t border-slate-100" />
                {/* Customize - stays open */}
                <button
                  onClick={() => { setShowCustom((v) => !v); setDateRange('custom'); }}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <span>{t('customize')}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={`transition-transform ${showCustom ? 'rotate-90' : ''}`}>
                    <path d="M6 4l4 4-4 4" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {/* Custom date inputs - inside dropdown */}
                {showCustom && (
                  <div className="flex items-center gap-2 overflow-hidden px-4 pb-3 pt-1">
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="h-9 w-0 min-w-0 flex-1 rounded-lg border border-slate-200 px-2 text-xs text-slate-700 focus:border-primary-400 focus:outline-none"
                    />
                    <span className="shrink-0 text-slate-400">—</span>
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="h-9 w-0 min-w-0 flex-1 rounded-lg border border-slate-200 px-2 text-xs text-slate-700 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="text-xs font-medium text-[#434655] sm:text-sm">{t('location')}:</span>
          <select
            value={selectedStoreId ?? 'all'}
            onChange={(e) => setSelectedStoreById(e.target.value === 'all' ? null : e.target.value)}
            className="h-9 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-3 pr-8 text-xs text-[#191B23] outline-none focus:ring-2 focus:ring-[#004AC6]/20 sm:flex-none sm:text-sm"
          >
            <option value="all">{t('allLocations')}</option>
            {availableStores.map((store) => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading state handled per-component */}

      {/* KPI Cards */}
      <div className="mt-6">
        {/* Bug #249 — icon thẻ Revenue phải nói cùng một đồng tiền với con số
            bên dưới. Phạm vi trộn nhiều loại tiền thì KHÔNG truyền gì: không có
            ký hiệu nào đúng cho cả bảng, vẽ đại một cái còn tệ hơn để trung
            tính (xem `useIsMixedCurrencyScope`). */}
        <ReportKPICards data={kpis} currency={isMixedCurrency ? null : activeCurrency} />
      </div>

      {/* Row 1: Revenue Over Time + Revenue by Region */}
      <div className="mt-6 grid grid-cols-12 gap-6 items-stretch">
        <div className="col-span-12 lg:col-span-8 flex flex-col">
          <RevenueOverTimeChart data={revenueOverTimeData} className="flex-1" />
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col">
          <RevenueByRegionPanel data={revenueByRegion} className="flex-1" />
        </div>
      </div>

      {/* Row 2: Daily Transactions Count */}
      <div className="mt-6">
        <DailyTransactionsChart data={dailyTransactions} />
      </div>

      {/* Row 3: Profit by Location + Top Profitable Products */}
      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6">
          <ProfitByLocationTable rows={profitByLocation} />
        </div>
        <div className="col-span-12 lg:col-span-6">
          <TopProfitableProductsTable products={topProducts} />
        </div>
      </div>

      {/* Row 4: Top Stolen Items (thất thoát) */}
      <div className="mt-6">
        <TopLostItemsTable items={topLostItems} />
      </div>
    </PageContainer>
  );
}
